// Centralizes the save/delete handlers for the catalog (products,
// suppliers, categories). Lifted out of CatalogPanel so the panel
// component is mostly layout, with much lower cyclomatic complexity.
import { useCallback } from "react";
import { toast } from "sonner";
import {
  addCategory,
  addProduct,
  addSupplier,
  removeCategory,
  removeProduct,
  removeSupplier,
  renameProductsCategory,
  updateCategory,
  updateProduct,
  updateSupplier,
} from "../lib/storage";

function storageErrorMessage(error) {
  const quota = error?.name === "QuotaExceededError" || error?.code === 22 || error?.code === 1014;
  if (quota) return "This device is out of catalog storage. Try a smaller product photo or remove an old uploaded photo.";
  return "Couldn't save the product. Please try again.";
}

export function useCatalogMutations({
  categories,
  productCountBySupplier,
  productCountByCategory,
  onProductsChange,
  onSuppliersChange,
  onCategoriesChange,
}) {
  const saveProduct = useCallback((p, isEdit) => {
    try {
      onProductsChange(isEdit ? updateProduct(p.id, p) : addProduct(p));
      toast.success(isEdit ? "Product updated" : "Product added");
      return true;
    } catch (error) {
      console.error("Failed to save product", error);
      toast.error(storageErrorMessage(error));
      return false;
    }
  }, [onProductsChange]);

  const deleteProduct = useCallback((id) => {
    onProductsChange(removeProduct(id));
    toast("Product removed");
  }, [onProductsChange]);

  const saveSupplier = useCallback((s, isEdit) => {
    onSuppliersChange(isEdit ? updateSupplier(s.id, s) : addSupplier(s));
    toast.success(isEdit ? "Vendor updated" : "Vendor added");
  }, [onSuppliersChange]);

  const deleteSupplier = useCallback((id) => {
    const count = productCountBySupplier[id] || 0;
    if (count > 0) {
      toast.error(`Vendor has ${count} product${count > 1 ? "s" : ""}. Reassign first.`);
      return;
    }
    onSuppliersChange(removeSupplier(id));
    toast("Vendor removed");
  }, [onSuppliersChange, productCountBySupplier]);

  const saveCategory = useCallback((c, isEdit, oldName) => {
    if (isEdit && oldName && oldName !== c.name) {
      onProductsChange(renameProductsCategory(oldName, c.name));
    }
    onCategoriesChange(isEdit ? updateCategory(c.id, c) : addCategory(c));
    toast.success(isEdit ? "Category updated" : "Category added");
  }, [onCategoriesChange, onProductsChange]);

  const deleteCategory = useCallback((id) => {
    const c = categories.find((x) => x.id === id);
    const count = c ? productCountByCategory[c.name] || 0 : 0;
    if (count > 0) {
      toast.error(`${c.name} has ${count} product${count > 1 ? "s" : ""}. Reassign first.`);
      return;
    }
    onCategoriesChange(removeCategory(id));
    toast("Category removed");
  }, [categories, productCountByCategory, onCategoriesChange]);

  return { saveProduct, deleteProduct, saveSupplier, deleteSupplier, saveCategory, deleteCategory };
}
